import { useQuery, useMutation } from "@tanstack/react-query";
import { useStore } from "@/lib/store";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "./ui/button";
import { Conversation } from "@shared/schema";
import { canAccessSitesModule, standardAllowedModules } from "@shared/module-access";
import {
  LogOut,
  User,
  Lock,
  Settings,
  Coins,
  Plus,
  BarChart3,
  Users,
  SlidersHorizontal,
  LayoutGrid,
  Newspaper,
  MessageSquare,
  Globe,
  Image as ImageIcon,
  BookImage,
  Workflow,
  CalendarDays,
  Scissors,
  Wand2,
  Sparkles,
  Inbox,
  Bot,
  PenLine,
  Lightbulb,
  Plug,
  Loader2,
} from "lucide-react";
import { JobBadge } from "./job-badge";
import { JobCompletionNotifier } from "./job-completion-notifier";
import { useActiveJobs } from "@/hooks/use-active-jobs";
import { apiRequest, queryClient } from "@/lib/queryClient";
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { motion } from "framer-motion";
import EditTitleDialog from "./edit-title-dialog";
import AnalyticsSettingsModal from "./analytics-settings-modal";
import ModulePreferencesModal from "./module-preferences-modal";
import { SidebarModuleList } from "./sidebar-module-list";
import { ChatHistoryBar } from "./ui/chat-history-bar";
import { PaywallModal } from "./paywall-modal";
import { ClientSelector } from "./client-selector";
import {
  GALLERY_CATEGORIES,
  GALLERY_CATEGORY_LABEL_KEYS,
  type GalleryCategory,
} from "./viral-gallery/types";
import { isProductionHost } from "@/lib/runtime-env";
import { readViewMode, writeViewMode, type AppViewMode } from "@/lib/view-mode";

import { getModuleIcon } from "@/utils/module-icons";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "./language-switcher";

export default function Sidebar() {
  const { toast } = useToast();
  const { t } = useTranslation();
  const {
    currentModule,
    currentChatId,
    setCurrentChatId,
    setCurrentModule,
    setMessages,
    modules,
    organicTab,
    setOrganicTab,
  } = useStore();

  const { user } = useAuth();

  // Selected offer sheet for new conversations

  // Filter and edit states
  const [moduleFilter, setModuleFilter] = useState<string>("all");
  // REKLAMY | ORGANIC view toggle — persists in localStorage (see @/lib/view-mode).
  const [viewMode, setViewMode] = useState<AppViewMode>(readViewMode);
  useEffect(() => {
    writeViewMode(viewMode);
  }, [viewMode]);
  // Plan Organic-only (brak jakiegokolwiek paid_ads.* w uprawnieniach) → wymuś widok
  // Organic i ukryj toggle „Reklamy". Null perms = brak restrykcji (super_admin itd.).
  const hasPaidAdsAccess = (() => {
    const perms = (user as { agencyPermissions?: unknown } | null)?.agencyPermissions;
    return (
      !Array.isArray(perms) || perms.some((p) => typeof p === "string" && p.startsWith("paid_ads."))
    );
  })();
  useEffect(() => {
    if (!hasPaidAdsAccess && viewMode !== "organic") setViewMode("organic");
  }, [hasPaidAdsAccess, viewMode]);
  const [editingConversation, setEditingConversation] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [editTitleValue, setEditTitleValue] = useState("");

  // Loading state for conversation that's being loaded
  const [loadingConversationId, setLoadingConversationId] = useState<string | null>(null);

  // Analytics settings modal state
  const [analyticsSettingsOpen, setAnalyticsSettingsOpen] = useState(false);

  // Module preferences modal state
  const [modulePreferencesOpen, setModulePreferencesOpen] = useState(false);

  // Paywall modal state
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallReason, setPaywallReason] = useState<{ title: string; description: string } | null>(
    null
  );

  // Dodajemy nowy stan do niezawodnego podświetlania konwersacji
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(
    currentChatId
  );

  // Listen for conversation loaded events from ChatInterface
  useEffect(() => {
    const handleConversationLoaded = (event: CustomEvent) => {
      if (event.detail.conversationId === loadingConversationId) {
        setLoadingConversationId(null);
      }
    };

    window.addEventListener("conversationLoaded", handleConversationLoaded as EventListener);
    return () =>
      window.removeEventListener("conversationLoaded", handleConversationLoaded as EventListener);
  }, [loadingConversationId]);

  // Prostszy, bardziej niezawodny event listener do oznaczania konwersacji jako załadowanych
  useEffect(() => {
    function markConversationLoaded(event: any) {
      if (event.detail?.conversationId) {
        setLoadingConversationId(null);
        try {
          useStore.getState().setLoading(false);
        } catch {}
      }
    }
    window.addEventListener("conversationLoaded", markConversationLoaded);
    return () => window.removeEventListener("conversationLoaded", markConversationLoaded);
  }, []);

  // Get current module data
  const currentModuleData = modules.find((m) => m.id === currentModule);

  // Fetch recent chats with optional module filter
  const { data: recentConversations = [] } = useQuery<Conversation[]>({
    queryKey: ["/api/conversations", moduleFilter === "all" ? undefined : moduleFilter],
    queryFn: () => {
      const params = moduleFilter === "all" ? "" : `?moduleId=${moduleFilter}`;
      return apiRequest("GET", `/api/conversations${params}`);
    },
  });

  // No need for placeholder logic - conversations appear only after first message
  // This matches ChatGPT behavior where conversations are created when you send first message

  const handleModuleChange = (moduleId: string, locked: boolean) => {
    if (locked) {
      setPaywallReason({
        title: t("paywall.moduleUnavailableTitle"),
        description: t("paywall.moduleUnavailableDescription"),
      });
      setPaywallOpen(true);
      return;
    }

    // If switching to different module, just set the module - don't create conversation yet
    if (moduleId !== currentModule) {
      setCurrentModule(moduleId);
      setCurrentChatId(null); // Clear current chat so new one will be created on first message
      setLocation(`/chat/${moduleId}`);
    }
  };

  const handleConversationSelect = (conversation: {
    id: string;
    title: string | null;
    moduleId: string;
    createdAt: Date | string | null;
  }) => {
    // NATYCHMIASTOWA REAKCJA UI - zawsze działa, bez wyjątków

    // 1. NATYCHMIAST PODŚWIETL ELEMENT - dwa niezależne stany zapewniają niezawodność
    setSelectedConversationId(conversation.id);
    setCurrentChatId(conversation.id);

    // 2. NATYCHMIAST POKAŻ SPINNER - zawsze widoczny
    setLoadingConversationId(conversation.id);

    // 3. NATYCHMIAST USTAW GLOBALNY STAN ŁADOWANIA - z zabezpieczeniem
    try {
      useStore.getState().setLoading(true);
    } catch {}

    // 4. NATYCHMIAST WYCZYŚĆ WIADOMOŚCI - pokazuje pusty stan zamiast starych wiadomości
    try {
      setMessages([]);
    } catch {}

    // 5. NATYCHMIAST ZMIEŃ MODUŁ
    setCurrentModule(conversation.moduleId);

    // 6. NATYCHMIAST POWIADOM INTERFEJS CZATU
    try {
      window.dispatchEvent(
        new CustomEvent("conversationSelected", {
          detail: { conversationId: conversation.id },
        })
      );
    } catch {}

    // 7. NATYCHMIAST ROZPOCZNIJ ŁADOWANIE DANYCH
    try {
      queryClient.prefetchQuery({
        queryKey: ["/api/conversations", conversation.id, "messages"],
        queryFn: () => apiRequest("GET", `/api/conversations/${conversation.id}/messages`),
      });
    } catch {}

    // 8. NIEZAWODNY FALLBACK - zawsze wyłącz ładowanie po określonym czasie
    // Zwiększamy timeout do 5 sekund, aby dać więcej czasu na ładowanie
    setTimeout(() => {
      if (loadingConversationId === conversation.id) {
        setLoadingConversationId(null);
        try {
          useStore.getState().setLoading(false);
        } catch {}
      }
    }, 5000);
  };

  const formatTimeAgo = (date: Date | string | null) => {
    if (!date) return "";
    const now = new Date();
    const chatDate = new Date(date);
    const diffInHours = Math.floor((now.getTime() - chatDate.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return t("time.now");
    if (diffInHours < 24) return t("time.hoursAgo", { count: diffInHours });
    if (diffInHours < 48) return t("time.yesterday");
    return t("time.daysAgo", { count: Math.floor(diffInHours / 24) });
  };

  // ===== Optimistic Conversations Panel =====
  const ConversationsPanel: React.FC<{
    conversations: Conversation[];
    activeId: string | null;
    loadingId: string | null;
  }> = ({ conversations, activeId, loadingId }) => {
    const onClickItem = (conversation: Conversation) => {
      handleConversationSelect(conversation);
    };
    const { jobs: liveJobs } = useActiveJobs();
    const JOB_LABEL: Record<string, string> = {
      "ad-graphic": "Generuję grafikę…",
      graphic: "Generuję grafikę…",
      video: "Generuję wideo…",
    };
    return (
      <div className="conversations-scroll flex-1 overflow-y-auto px-0">
        <ul className="space-y-0.5 w-full px-0">
          {/* Active background generations — click to return to the exact generating view */}
          {liveJobs.map((job) => (
            <li
              key={`job-${job.id}`}
              className="conv-item relative group rounded-lg cursor-pointer py-2 w-full px-0"
              onClick={() => setLocation(job.deepLink)}
              tabIndex={0}
              role="button"
              aria-label="Generacja w toku — wróć"
            >
              <span className="absolute inset-0 rounded-lg bg-emerald-500/[0.06] group-hover:bg-emerald-500/[0.12] pointer-events-none" />
              <div className="relative flex items-center space-x-3">
                <span className="icon-wrap inline-flex items-center justify-center rounded-md flex-shrink-0">
                  <Loader2 className="w-3 h-3 animate-spin text-emerald-400" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className="text-white text-sm font-medium truncate leading-tight">
                      {JOB_LABEL[job.type] ?? "Generuję…"}
                    </p>
                    <span className="text-emerald-300/80 text-xs animate-pulse">w trakcie</span>
                  </div>
                  <div className="flex items-center space-x-1 mt-0.5">
                    <span className="text-emerald-300/60 text-xs">kliknij, by wrócić</span>
                    {typeof job.progress === "number" && job.progress > 0 && (
                      <>
                        <span className="text-white/30 text-xs">•</span>
                        <span className="text-white/40 text-xs">{job.progress}%</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </li>
          ))}
          {conversations.map((conversation) => {
            const isActive =
              activeId === conversation.id || selectedConversationId === conversation.id;
            const isLoading = loadingId === conversation.id;
            return (
              <li
                key={conversation.id}
                className={`conv-item relative group rounded-lg cursor-pointer transition-all duration-75 py-2 w-full px-0 ${
                  isActive ? "ring-1 ring-red-500/50 conv-item-active bg-red-900/20" : ""
                } ${isLoading ? "opacity-90" : ""}`}
                onClick={() => onClickItem(conversation)}
                tabIndex={0}
                aria-label={conversation.title || t("sidebar.newConversation")}
                role="button"
              >
                <span
                  className={`absolute inset-0 rounded-lg transition-colors duration-75 pointer-events-none ${
                    isActive
                      ? "bg-gradient-to-r from-red-900/40 to-red-800/30"
                      : "bg-transparent group-hover:bg-white/[0.04]"
                  }`}
                />
                <div className="relative flex items-center space-x-3">
                  <span className="icon-wrap inline-flex items-center justify-center rounded-md flex-shrink-0">
                    {isLoading ? (
                      <svg
                        className="w-3 h-3 animate-spin text-red-400"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    ) : (
                      getModuleIcon(conversation.moduleId, "w-3 h-3", "conversation")
                    )}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <p className="text-white text-sm font-medium truncate leading-tight">
                        {conversation.title || t("sidebar.newConversation")}
                      </p>
                      {isLoading && (
                        <span className="text-red-400 text-xs animate-pulse">
                          {t("common.loading")}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-1 mt-0.5">
                      <span className="text-white/40 text-xs">
                        {modules.find((m) => m.id === conversation.moduleId)?.name ||
                          conversation.moduleId}
                      </span>
                      <span className="text-white/30 text-xs">•</span>
                      <span className="text-white/40 text-xs">
                        {formatTimeAgo(conversation.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
          {conversations.length === 0 && (
            <li className="text-center py-8">
              <MessageSquare className="w-8 h-8 text-white/30 mx-auto mb-2" />
              <p className="text-white/40 text-xs">{t("sidebar.noRecentConversations")}</p>
              <p className="text-white/30 text-xs">{t("sidebar.startNewConversation")}</p>
            </li>
          )}
        </ul>
      </div>
    );
  };

  // Handle title editing
  const handleEditTitle = (conversationId: string, currentTitle: string) => {
    setEditingConversation({ id: conversationId, title: currentTitle });
    setEditTitleValue(currentTitle);
  };

  // Handle conversation deletion
  const deleteConversationMutation = useMutation({
    mutationFn: (conversationId: string) =>
      apiRequest("DELETE", `/api/conversations/${conversationId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/conversations"] });
      toast({
        title: t("sidebar.conversationDeleted"),
        description: t("sidebar.conversationDeletedDesc"),
      });
    },
    onError: () => {
      toast({
        title: t("common.error"),
        description: t("sidebar.deleteError"),
        variant: "destructive",
      });
    },
  });

  const handleDeleteConversation = (conversationId: string, conversationTitle: string) => {
    if (
      confirm(
        t("sidebar.deleteConfirm", { title: conversationTitle || t("sidebar.newConversation") })
      )
    ) {
      const isActive =
        conversationId === currentChatId || conversationId === selectedConversationId;
      deleteConversationMutation.mutate(conversationId);
      if (isActive) {
        setCurrentChatId(null);
        setSelectedConversationId(null);
      }
    }
  };

  const logoutMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/auth/logout"),
    onSuccess: () => {
      window.location.href = "/";
    },
    onError: () => {
      window.location.href = "/";
    },
  });

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  // Edit title and save functions already defined above

  // Check if user has restricted access (dashboard-only or limited modules).
  // Opt-in modules (e.g. 'sites') are stripped first — see shared/module-access.ts.
  const allowedModules = standardAllowedModules((user as any)?.allowedModules ?? null);
  const hiddenModules: string[] | null = (user as any)?.hiddenModules ?? null;
  const hiddenByUser = new Set(hiddenModules ?? []);
  const isDashboardOnly =
    allowedModules !== null && allowedModules.length === 1 && allowedModules[0] === "dashboard";
  const hasModuleAccess = allowedModules === null || allowedModules.some((m) => m !== "dashboard");
  const hasDashboardAccess = allowedModules === null || allowedModules.includes("dashboard");
  const userRole = (user as any)?.role;
  const userEmail = ((user as any)?.email ?? "").toLowerCase();
  // Beta testerzy autopilota — widzą Autopilot Ads + Firm Assets w sidebarze (bez super_admin).
  // Lista white-listed maili, NIE nadajemy super_admin role.
  const AUTOPILOT_BETA_EMAILS = new Set(["mar.horowski@gmail.com"]);
  const isAutopilotBetaTester = userRole === "super_admin" || AUTOPILOT_BETA_EMAILS.has(userEmail);
  // Dostęp do modułu "Strony" — super_admin lub jawny wpis 'sites' w allowed_modules
  // (ten sam helper co backendowy middleware requireSitesAccess).
  const isSitesAllowed = canAccessSitesModule(
    user as { role?: string | null; allowedModules?: string[] | null } | null
  );
  // Employees with agency_panel permission use the agency panel, not the manager dashboard
  const agencyPermissions: string[] | null = (user as any)?.agencyPermissions ?? null;
  const hasAgencyPanelPerm =
    Array.isArray(agencyPermissions) && agencyPermissions.includes("agency_panel");
  const [location, setLocation] = useLocation();
  const isDashboardActive = location === "/dashboard" || currentModule === "dashboard";
  const isLeadsActive = location === "/leads";

  return (
    <>
      <motion.aside
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="w-72 lg:w-80 bg-black/30 backdrop-blur-2xl border-r border-white/[0.04] flex flex-col h-full text-white relative"
      >
        {/* Logo + Client Selector row — offset right to not overlap toggle button */}
        <div className="pl-20 pr-5 pt-4 pb-3 shrink-0 flex items-center gap-2">
          <div className="relative inline-block shrink-0">
            <button
              type="button"
              // VP-120: logo prowadzi na home AKTUALNEGO trybu i go zachowuje —
              // nie wolno mu przełączać Organic ↔ Reklamy. Lustro handlerów toggle
              // (linie ~432/450), ale BEZ setViewMode, więc tryb zostaje bez zmian.
              onClick={() => {
                setOrganicTab(null);
                setCurrentChatId(null);
                if (viewMode === "organic") {
                  setCurrentModule("organic-home");
                  setLocation("/viralbooster");
                } else {
                  setCurrentModule("home");
                  setLocation("/chat/home");
                }
              }}
              className="inline-block group cursor-pointer"
            >
              <img
                src="https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/Viral+Partners+App+Logo.png"
                alt="Viral Partners"
                className="w-10 h-10 object-contain rounded-xl group-hover:scale-105 transition-transform"
              />
            </button>
            <JobBadge />
            <JobCompletionNotifier />
          </div>
          <div className="flex-1 min-w-0">
            <ClientSelector />
          </div>
        </div>

        <div className="h-px bg-white/[0.06] mx-4 shrink-0" />

        {/* REKLAMY | ORGANIC view toggle — ukryty dla planu Organic-only (brak paid_ads). */}
        {hasModuleAccess && hasPaidAdsAccess && (
          <div className="px-4 pt-3 pb-1 shrink-0">
            <div className="flex bg-white/[0.04] border border-white/[0.06] rounded-xl p-0.5">
              <button
                type="button"
                onClick={() => {
                  setViewMode("reklamy");
                  // Switching to Reklamy lands on the welcome/home screen (symmetric to Organic).
                  setOrganicTab(null);
                  setCurrentChatId(null);
                  setCurrentModule("home");
                  setLocation("/chat/home");
                }}
                className={`flex-1 px-3 py-1.5 rounded-lg text-[11px] font-semibold tracking-wide uppercase transition-all ${
                  viewMode === "reklamy"
                    ? "bg-[#ef3e29] text-white shadow-[0_0_12px_rgba(239,62,41,0.35)]"
                    : "text-white/45 hover:text-white/75"
                }`}
              >
                Reklamy
              </button>
              <button
                type="button"
                onClick={() => {
                  setViewMode("organic");
                  // VP-105: switching to Organic lands on the ViralBooster agent (/viralbooster).
                  setOrganicTab(null);
                  setCurrentChatId(null);
                  setCurrentModule("organic-home");
                  setLocation("/viralbooster");
                }}
                className={`flex-1 px-3 py-1.5 rounded-lg text-[11px] font-semibold tracking-wide uppercase transition-all ${
                  viewMode === "organic"
                    ? "bg-emerald-600 text-white shadow-[0_0_12px_rgba(16,185,129,0.35)]"
                    : "text-white/45 hover:text-white/75"
                }`}
              >
                Organic
              </button>
            </div>
          </div>
        )}

        {hasModuleAccess && (
          <>
            {/* Scrollable middle: modules */}
            <div className="flex-1 overflow-y-auto min-h-0 py-2">
              <div className="px-3 space-y-0.5">
                {/* Ads Wizard (VP-188) — hub „idiotoodpornych" kreatorów reklam (kafelki
                    Facebook/Google/TikTok); tor Facebook = onboarding-wizard (VP-175).
                    REKLAMY only. Ukryte na produkcji (app.viral.partners) — feature żyje
                    tylko na dev/localhost. */}
                {viewMode === "reklamy" &&
                  !isProductionHost() &&
                  (() => {
                    const adsWizardActive = [
                      "ads-wizard",
                      "onboarding-wizard",
                      "google-ads-wizard",
                    ].includes(currentModule);
                    return (
                      <button
                        data-testid="sidebar-ads-wizard"
                        onClick={() => handleModuleChange("ads-wizard", false)}
                        className={`flex items-center gap-3 px-3 py-2 rounded-xl w-full text-left group transition-colors duration-100 ${
                          adsWizardActive ? "bg-[#ef3e29]/10" : "hover:bg-white/[0.05]"
                        }`}
                      >
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-100 ${
                            adsWizardActive
                              ? "bg-[#ef3e29]/20"
                              : "bg-white/[0.06] group-hover:bg-white/[0.1]"
                          }`}
                        >
                          <Wand2
                            className={`w-4 h-4 ${adsWizardActive ? "text-[#ef3e29]" : "text-white/60"}`}
                          />
                        </div>
                        <span
                          className={`text-sm font-medium transition-colors duration-100 truncate ${
                            adsWizardActive
                              ? "text-white"
                              : "text-white/60 group-hover:text-white/90"
                          }`}
                        >
                          Ads Wizard
                        </span>
                      </button>
                    );
                  })()}
                {/* Dashboard — REKLAMY only */}
                {viewMode === "reklamy" && hasDashboardAccess && (
                  <button
                    onClick={() => {
                      setCurrentModule("dashboard");
                      setCurrentChatId(null);
                      setLocation("/chat/dashboard");
                    }}
                    className={`flex items-center gap-3 px-3 py-2 rounded-xl w-full text-left group transition-colors duration-100 ${
                      isDashboardActive ? "bg-[#ef3e29]/10" : "hover:bg-white/[0.05]"
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-100 ${
                        isDashboardActive
                          ? "bg-[#ef3e29]/20"
                          : "bg-white/[0.06] group-hover:bg-white/[0.1]"
                      }`}
                    >
                      <BarChart3
                        className={`w-4 h-4 ${isDashboardActive ? "text-[#ef3e29]" : "text-white/60"}`}
                      />
                    </div>
                    <span
                      className={`text-sm font-medium transition-colors duration-100 truncate ${
                        isDashboardActive ? "text-white" : "text-white/60 group-hover:text-white/90"
                      }`}
                    >
                      {t("sidebar.dashboard")}
                    </span>
                  </button>
                )}
                {/* Leads (Sales Data) — REKLAMY only; Monitoring przeniesiony do zakładki dashboardu */}
                {viewMode === "reklamy" && (
                  <>
                    <button
                      onClick={() => {
                        setCurrentChatId(null);
                        setLocation("/leads");
                      }}
                      className={`flex items-center gap-3 px-3 py-2 rounded-xl w-full text-left group transition-colors duration-100 ${
                        isLeadsActive ? "bg-[#6E3AFF]/10" : "hover:bg-white/[0.05]"
                      }`}
                    >
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-100 ${
                          isLeadsActive
                            ? "bg-[#6E3AFF]/20"
                            : "bg-white/[0.06] group-hover:bg-white/[0.1]"
                        }`}
                      >
                        <Users
                          className={`w-4 h-4 ${isLeadsActive ? "text-[#6E3AFF]" : "text-white/60"}`}
                        />
                      </div>
                      <span
                        className={`text-sm font-medium transition-colors duration-100 truncate ${
                          isLeadsActive ? "text-white" : "text-white/60 group-hover:text-white/90"
                        }`}
                      >
                        {t("sidebar.salesData")}
                      </span>
                    </button>
                  </>
                )}
                {/* Strony — hosted sites — REKLAMY only, super_admin + whitelist */}
                {viewMode === "reklamy" && isSitesAllowed && (
                  <button
                    data-testid="sidebar-sites"
                    onClick={() => {
                      setCurrentChatId(null);
                      setLocation("/strony");
                    }}
                    className={`flex items-center px-3 py-2 rounded-xl w-full text-left group transition-colors duration-100 ${
                      location === "/strony" ? "bg-[#ef3e29]/10" : "hover:bg-white/[0.05]"
                    }`}
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0">
                      <Globe
                        className={`w-5 h-5 ${location === "/strony" ? "text-white" : "text-white/60 group-hover:text-white/90"}`}
                      />
                    </div>
                    <span
                      className={`ml-2 text-sm font-medium transition-colors duration-100 truncate ${
                        location === "/strony"
                          ? "text-white"
                          : "text-white/60 group-hover:text-white/90"
                      }`}
                    >
                      {t("sidebar.sites")}
                    </span>
                  </button>
                )}
                {/* Viral Gallery — Higgsfield-style inspiracje (REKLAMY only) */}
                {viewMode === "reklamy" && (
                  <>
                    <button
                      data-testid="sidebar-viral-gallery"
                      onClick={() => {
                        setCurrentChatId(null);
                        setLocation("/viral");
                      }}
                      className={`flex items-center px-3 py-2 rounded-xl w-full text-left group transition-colors duration-100 ${
                        location === "/viral" ? "bg-[#ef3e29]/10" : "hover:bg-white/[0.05]"
                      }`}
                    >
                      <div className="w-full h-8 rounded-lg overflow-hidden transition-colors duration-100">
                        <img
                          src="https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/Zaso%CC%81b+25%402x.png"
                          alt="Viral Gallery"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </button>
                    {location === "/viral" && <ViralGalleryCategoryList />}
                  </>
                )}
                {/* Autopilot Ads — REKLAMY only. Super_admin + beta testerzy whitelist. */}
                {viewMode === "reklamy" && isAutopilotBetaTester && (
                  <button
                    data-testid="sidebar-autopilot"
                    onClick={() => {
                      setCurrentChatId(null);
                      setLocation("/admin/autopilot");
                    }}
                    className={`flex items-center px-3 py-2 rounded-xl w-full text-left group transition-colors duration-100 ${
                      location.startsWith("/admin/autopilot")
                        ? "bg-cyan-500/10"
                        : "hover:bg-white/[0.05]"
                    }`}
                  >
                    <div
                      className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${
                        location.startsWith("/admin/autopilot")
                          ? "bg-cyan-500/20"
                          : "bg-white/[0.06] group-hover:bg-white/[0.1]"
                      }`}
                    >
                      <Bot
                        className={`w-4 h-4 ${location.startsWith("/admin/autopilot") ? "text-cyan-300" : "text-white/60 group-hover:text-white/90"}`}
                      />
                    </div>
                    <span
                      className={`ml-2 text-sm font-medium transition-colors duration-100 truncate ${
                        location.startsWith("/admin/autopilot")
                          ? "text-white"
                          : "text-white/60 group-hover:text-white/90"
                      }`}
                    >
                      Autopilot Ads
                    </span>
                  </button>
                )}
                {/* Firm Assets (brand library) — REKLAMY only. Super_admin + beta testerzy whitelist. */}
                {viewMode === "reklamy" && isAutopilotBetaTester && (
                  <button
                    data-testid="sidebar-firm-assets"
                    onClick={() => {
                      setCurrentChatId(null);
                      setLocation("/firm-assets");
                    }}
                    className={`flex items-center px-3 py-2 rounded-xl w-full text-left group transition-colors duration-100 ${
                      location === "/firm-assets" ? "bg-cyan-500/10" : "hover:bg-white/[0.05]"
                    }`}
                  >
                    <div
                      className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${
                        location === "/firm-assets"
                          ? "bg-cyan-500/20"
                          : "bg-white/[0.06] group-hover:bg-white/[0.1]"
                      }`}
                    >
                      <Sparkles
                        className={`w-4 h-4 ${location === "/firm-assets" ? "text-cyan-300" : "text-white/60 group-hover:text-white/90"}`}
                      />
                    </div>
                    <span
                      className={`ml-2 text-sm font-medium transition-colors duration-100 truncate ${
                        location === "/firm-assets"
                          ? "text-white"
                          : "text-white/60 group-hover:text-white/90"
                      }`}
                    >
                      Firm Assets
                    </span>
                  </button>
                )}
                {/* Module list — in ORGANIC mode hide ad-only modules; content-creator tabs render below. */}
                <SidebarModuleList
                  modules={modules.map((module) => ({ ...module, locked: Boolean(module.locked) }))}
                  currentModule={currentModule}
                  onModuleSelect={handleModuleChange}
                  hiddenModuleIds={(() => {
                    const hidden = new Set(hiddenByUser);
                    // content-creator is always surfaced via tabs below in ORGANIC, irrelevant in REKLAMY.
                    hidden.add("content-creator");
                    // Wyjęte z nawigacji: VSL usunięty z UI; arkusz idealnego klienta żyje w Ustawieniach.
                    hidden.add("vsl-creator");
                    hidden.add("offer-builder");
                    if (viewMode === "organic") {
                      [
                        "ad-creator",
                        "marketing-analyst",
                        "campaign-creator",
                        "allegro-ads",
                      ].forEach((id) => hidden.add(id));
                    }
                    return hidden;
                  })()}
                />
                {/* Organic content tabs — rendered as nav items when in ORGANIC view */}
                {viewMode === "organic" &&
                  (() => {
                    const isOnContentCreator =
                      location.startsWith("/chat/content-creator") ||
                      currentModule === "content-creator" ||
                      currentModule === "reel-creator" ||
                      currentModule === "stories-creator" ||
                      currentModule === "carousel-creator" ||
                      currentModule === "youtube-mode";
                    const activeTab = isOnContentCreator ? organicTab : null;
                    // Other tabs (URL ?tab=). Tabs marked superAdminOnly are hidden
                    // for non-super_admin users (mirrors SUPER_ADMIN_ONLY_TABS in
                    // pages/content-creator.tsx — keep these two lists in sync).
                    const isSuperAdmin =
                      (user as { role?: string } | null | undefined)?.role === "super_admin";
                    const ORGANIC_TABS: {
                      id: string;
                      label: string;
                      icon: React.ReactNode;
                      superAdminOnly?: boolean;
                    }[] = [
                      { id: "graphics", label: "Grafiki", icon: <ImageIcon className="w-4 h-4" /> },
                      {
                        id: "library",
                        label: "Biblioteka",
                        icon: <BookImage className="w-4 h-4" />,
                      },
                      { id: "pipeline", label: "Pipeline", icon: <Workflow className="w-4 h-4" /> },
                      {
                        id: "scheduler",
                        label: "Scheduler",
                        icon: <CalendarDays className="w-4 h-4" />,
                      },
                      {
                        id: "montaz",
                        label: "Montaż",
                        icon: <Scissors className="w-4 h-4" />,
                        superAdminOnly: true,
                      },
                      {
                        id: "generator",
                        label: "Generator wideo",
                        icon: <Wand2 className="w-4 h-4" />,
                        superAdminOnly: true,
                      },
                      {
                        id: "motion-graphics",
                        label: "Motion Graphics",
                        icon: <Sparkles className="w-4 h-4" />,
                        superAdminOnly: true,
                      },
                      {
                        id: "inbox",
                        label: "Inbox",
                        icon: <Inbox className="w-4 h-4" />,
                        superAdminOnly: true,
                      },
                      {
                        id: "automations",
                        label: "Asystent wiadomości",
                        icon: <Bot className="w-4 h-4" />,
                      },
                      {
                        id: "analytics",
                        label: "Analityka",
                        icon: <BarChart3 className="w-4 h-4" />,
                      },
                    ].filter((tab) => isSuperAdmin || !tab.superAdminOnly);
                    return (
                      <div className="mt-2 pt-2 border-t border-white/[0.06]">
                        <div className="px-3 py-1 text-[9px] uppercase tracking-wider text-emerald-400 dark:text-emerald-400/70 font-semibold">
                          Twórz
                        </div>
                        {/* VP-102: entry to the "Stwórz treść" format-picker landing */}
                        {(() => {
                          const isActive = currentModule === "organic-home";
                          return (
                            <button
                              onClick={() => {
                                setOrganicTab(null);
                                setCurrentChatId(null);
                                setCurrentModule("organic-home");
                                setLocation("/viralbooster");
                              }}
                              className={`flex items-center gap-3 px-3 py-2 rounded-xl w-full text-left group transition-colors duration-100 ${
                                isActive ? "bg-emerald-600/10" : "hover:bg-white/[0.05]"
                              }`}
                            >
                              <div
                                className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                  isActive
                                    ? "bg-emerald-500/20 text-emerald-300"
                                    : "bg-white/[0.06] text-white/60 group-hover:bg-white/[0.1]"
                                }`}
                              >
                                <Sparkles className="w-4 h-4" />
                              </div>
                              <span
                                className={`text-sm font-medium truncate ${
                                  isActive
                                    ? "text-white"
                                    : "text-white/60 group-hover:text-white/90"
                                }`}
                              >
                                ViralBooster
                              </span>
                            </button>
                          );
                        })()}
                        <div className="px-3 pt-3 pb-1 text-[9px] uppercase tracking-wider text-emerald-400 dark:text-emerald-400/70 font-semibold">
                          Produkcja
                        </div>
                        {ORGANIC_TABS.map((tab) => {
                          const isActive = isOnContentCreator && activeTab === tab.id;
                          return (
                            <button
                              key={tab.id}
                              onClick={() => {
                                setOrganicTab(tab.id);
                                setCurrentModule("content-creator");
                                setCurrentChatId(null);
                                setLocation("/chat/content-creator");
                              }}
                              className={`flex items-center gap-3 px-3 py-2 rounded-xl w-full text-left group transition-colors duration-100 ${
                                isActive ? "bg-emerald-600/10" : "hover:bg-white/[0.05]"
                              }`}
                            >
                              <div
                                className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                  isActive
                                    ? "bg-emerald-500/20 text-emerald-300"
                                    : "bg-white/[0.06] text-white/60 group-hover:bg-white/[0.1]"
                                }`}
                              >
                                {tab.icon}
                              </div>
                              <span
                                className={`text-sm font-medium truncate ${
                                  isActive
                                    ? "text-white"
                                    : "text-white/60 group-hover:text-white/90"
                                }`}
                              >
                                {tab.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}
              </div>

              {/* Token Display - mobile only */}
              {user && typeof user.tokensRemaining === "number" && (
                <div className="px-3 py-3 md:hidden">
                  <div className="bg-white/[0.04] rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center">
                          <Coins className="w-2 h-2 text-white" />
                        </div>
                        <span className="text-white/50 text-xs">{t("common.tokens")}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`font-semibold text-sm ${
                            user.tokensRemaining < 50
                              ? "text-red-400"
                              : user.tokensRemaining < 200
                                ? "text-yellow-400"
                                : "text-red-400"
                          }`}
                        >
                          {user.tokensRemaining.toLocaleString()} /{" "}
                          {(
                            (user as any).monthlyTokenAllocation || user.tokensRemaining
                          ).toLocaleString()}
                        </span>
                        {/* VP-256: dokupienie tokenów wprost przy liczniku — klient nie
                            mógł znaleźć, gdzie je kupić. */}
                        <button
                          onClick={() => setLocation("/settings?tab=billing")}
                          title={t("common.buyTokens")}
                          aria-label={t("common.buyTokens")}
                          className="w-5 h-5 rounded-md bg-[#ef3e29]/15 border border-[#ef3e29]/25 text-[#ef3e29] hover:bg-[#ef3e29]/25 transition-colors flex items-center justify-center"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* end scrollable middle */}

            {/* Conversations — expandable, pinned */}
            <div className="shrink-0 py-2">
              <ChatHistoryBar
                conversations={recentConversations}
                modules={modules.map((module) => ({
                  id: module.id,
                  name: module.name,
                  locked: Boolean(module.locked),
                }))}
                activeId={currentChatId || selectedConversationId}
                loadingId={loadingConversationId}
                moduleFilter={moduleFilter}
                onModuleFilterChange={setModuleFilter}
                onConversationSelect={handleConversationSelect}
                onConversationDelete={handleDeleteConversation}
                hiddenModuleIds={hiddenByUser}
              />
            </div>
          </>
        )}

        {/* Bottom — user info */}
        <div className="px-4 py-3 border-t border-white/[0.06] space-y-2.5 shrink-0">
          {user && (
            <>
              {/* Tokens — desktop */}
              {hasModuleAccess && typeof user.tokensRemaining === "number" && (
                <div className="hidden md:flex items-center justify-between text-xs">
                  <div className="flex items-center space-x-2">
                    <div className="w-3.5 h-3.5 bg-[#ef3e29] rounded-full flex items-center justify-center">
                      <Coins className="w-2 h-2 text-white" />
                    </div>
                    <span className="text-white/40">{t("common.tokens")}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`font-medium ${
                        user.tokensRemaining < 50
                          ? "text-red-400"
                          : user.tokensRemaining < 200
                            ? "text-yellow-400"
                            : "text-white/50"
                      }`}
                    >
                      {user.tokensRemaining.toLocaleString()} /{" "}
                      {(
                        (user as any).monthlyTokenAllocation || user.tokensRemaining
                      ).toLocaleString()}
                    </span>
                    {/* VP-256: skrót „dokup tokeny" tuż przy liczniku. */}
                    <button
                      onClick={() => setLocation("/settings?tab=billing")}
                      title={t("common.buyTokens")}
                      aria-label={t("common.buyTokens")}
                      className="w-4 h-4 rounded bg-[#ef3e29]/15 border border-[#ef3e29]/25 text-[#ef3e29] hover:bg-[#ef3e29]/25 transition-colors flex items-center justify-center"
                    >
                      <Plus className="w-2.5 h-2.5" />
                    </button>
                  </div>
                </div>
              )}

              {/* User row */}
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-[#ef3e29]/80 rounded-full flex items-center justify-center flex-shrink-0">
                  <User size={11} className="text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white/80 text-xs font-medium truncate">{user.email}</p>
                  <p className="text-white/30 text-[10px]">
                    {t(
                      `roles.${
                        {
                          super_admin: "superAdmin",
                          agency_owner: "agencyOwner",
                          employee: "employee",
                          manager: "manager",
                        }[(user as any)?.role as string] ?? "client"
                      }`
                    )}
                  </p>
                </div>
                {/* Action icons */}
                <div className="flex items-center space-x-0.5 flex-shrink-0">
                  {hasModuleAccess && (
                    <button
                      onClick={() => setModulePreferencesOpen(true)}
                      className="text-white/30 hover:text-[#ef3e29] p-1 rounded transition-colors"
                      title={t("sidebar.moduleVisibility")}
                    >
                      <LayoutGrid size={12} />
                    </button>
                  )}
                  {/* Centrum Integracji — visible to ALL authenticated users
                    (including dashboard-only) since /integracje is the single
                    entry point for connecting platforms regardless of plan.
                    VP-300: w trybie ORGANIC hub otwiera się zawężony do publikacji. */}
                  <Link
                    href={viewMode === "organic" ? "/integracje?context=organic" : "/integracje"}
                  >
                    <button
                      className="text-white/30 hover:text-[#ef3e29] p-1 rounded transition-colors"
                      title={t("sidebar.integrations", "Centrum Integracji")}
                    >
                      <Plug size={12} />
                    </button>
                  </Link>
                  {(user as any)?.role === "super_admin" && (
                    <>
                      <Link href="/admin">
                        <button
                          className="text-white/30 hover:text-white p-1 rounded transition-colors"
                          title={t("roles.superAdmin")}
                        >
                          <Settings size={12} />
                        </button>
                      </Link>
                      <Link href="/changelog-admin">
                        <button
                          className="text-white/30 hover:text-orange-400 p-1 rounded transition-colors"
                          title={t("sidebar.changelog")}
                        >
                          <Newspaper size={12} />
                        </button>
                      </Link>
                    </>
                  )}
                  {((user as any)?.role === "agency_owner" ||
                    (user as any)?.role === "super_admin" ||
                    hasAgencyPanelPerm) && (
                    <Link href="/agency-admin">
                      <button
                        className="text-white/30 hover:text-purple-400 p-1 rounded transition-colors"
                        title={t("sidebar.agencyPanel")}
                      >
                        <Users size={12} />
                      </button>
                    </Link>
                  )}
                  <LanguageSwitcher />
                  <button
                    onClick={() => setLocation("/settings")}
                    className="text-white/30 hover:text-white p-1 rounded transition-colors"
                    title={t("settings.title")}
                  >
                    <Settings size={12} />
                  </button>
                  <button
                    onClick={handleLogout}
                    className="text-white/30 hover:text-red-400 p-1 rounded transition-colors"
                    title={t("auth.logout")}
                  >
                    <LogOut size={12} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </motion.aside>

      {/* Analytics Settings Modal */}
      <AnalyticsSettingsModal
        open={analyticsSettingsOpen}
        onOpenChange={setAnalyticsSettingsOpen}
      />

      {/* Module Preferences Modal */}
      <ModulePreferencesModal
        open={modulePreferencesOpen}
        onOpenChange={setModulePreferencesOpen}
      />

      {/* Title Edit Dialog */}
      <EditTitleDialog
        conversationId={editingConversation?.id || null}
        currentTitle={editingConversation?.title || ""}
        isOpen={!!editingConversation}
        onClose={() => setEditingConversation(null)}
      />

      {/* Paywall Modal */}
      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        title={paywallReason?.title}
        description={paywallReason?.description}
      />
    </>
  );
}

// ── Viral Gallery — sidebar category list ─────────────────────────────
function ViralGalleryCategoryList() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const [search, setSearch] = useState<string>(() =>
    typeof window === "undefined" ? "" : window.location.search
  );

  // Keep in sync with URL changes triggered elsewhere (history nav, page itself)
  useEffect(() => {
    const sync = () => setSearch(window.location.search);
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);

  const activeCategory = new URLSearchParams(search).get("category");

  type SidebarTab = { id: "all" | GalleryCategory; label: string };
  const TABS: SidebarTab[] = [
    { id: "all", label: t("viralGallery.all", "Wszystkie") },
    ...GALLERY_CATEGORIES.map((c) => ({ id: c, label: t(GALLERY_CATEGORY_LABEL_KEYS[c]) })),
  ];

  const handleClick = (id: SidebarTab["id"]) => {
    const params = new URLSearchParams();
    if (id !== "all") params.set("category", id);
    const qs = params.toString();
    const target = qs ? `/viral?${qs}` : "/viral";
    setLocation(target, { replace: true });
    setSearch(qs ? `?${qs}` : "");
  };

  const isActive = (id: SidebarTab["id"]) =>
    id === "all" ? !activeCategory : activeCategory === id;

  return (
    <div className="flex flex-col gap-0.5 pl-3 pr-1 py-1">
      {TABS.map((tab) => (
        <button
          key={tab.id}
          onClick={() => handleClick(tab.id)}
          className={`text-left text-[12px] px-3 py-1.5 rounded-lg transition-colors duration-100 truncate ${
            isActive(tab.id)
              ? "bg-[#ef3e29]/10 text-[#ef3e29]"
              : "text-white/55 hover:text-white/90 hover:bg-white/[0.04]"
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}
